/**
 * PaymentSuccessPage - i18n wiring (Phase 5)
 *
 * Translation namespaces used:
 *   - paymentSuccess.*  (new namespace added in Phase 5)
 *
 * Hardcoded values left in place (intentional):
 *   - "Stripe" provider brand name (passed through interpolation)
 *
 * Stripe is the only online payment provider. Confirmation is driven by the
 * server (GET /payments/status/{session_id} verifies the Checkout Session with
 * Stripe); a URL without a session_id never renders success.
 *   - euro symbol "€" alongside numeric amount (formatting handled at render time)
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Seo from '../components/Seo';
import { seo } from '../seo/seoData';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Loader2, ArrowRight, Euro } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = '/api';

const PaymentSuccessPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // Handle Stripe session
    if (!sessionId) {
      setStatus('error');
      setError(t('paymentSuccess.sessionNotFound'));
      return;
    }

    pollStripePaymentStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const pollStripePaymentStatus = async (attempts = 0) => {
    const maxAttempts = 30;
    const pollInterval = 3000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/payments/status/${sessionId}`);
      const data = response.data;

      if (data.status === 'paid') {
        setStatus('success');
        setPaymentData({ ...data, provider: 'stripe' });
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setTimeout(() => pollStripePaymentStatus(attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment:', err);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollStripePaymentStatus(attempts + 1), pollInterval);
      } else {
        setStatus('error');
        setError(t('paymentSuccess.paymentVerifyError'));
      }
    }
  };

  return (
    <div className="min-h-screen bg-black pt-20" data-testid="payment-success-page">
          <Seo {...seo.paymentSuccess} />
      <section className="py-24 md:py-32">
        <div className="max-w-[600px] mx-auto px-6">
          {status === 'loading' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="glass p-12 text-center"
            >
              <Loader2 className="w-16 h-16 text-white mx-auto mb-6 animate-spin" />
              <h1 className="font-heading text-2xl font-bold text-white mb-2">
                {t('paymentSuccess.verifying')}
              </h1>
              <p className="text-neutral-400">
                {t('paymentSuccess.verifyingDesc')}
              </p>
            </motion.div>
          )}

          {status === 'success' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass p-12 text-center"
            >
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h1 className="font-heading text-3xl font-bold text-white mb-2">
                {t('paymentSuccess.paymentConfirmed')}
              </h1>
              <p className="text-neutral-400 mb-8">
                {t('paymentSuccess.bookingCreated')}
              </p>

              {paymentData && (
                <div className="bg-white/5 p-6 mb-8 text-left space-y-4">
                  <div className="flex items-center gap-3">
                    <Euro className="w-5 h-5 text-neutral-500" />
                    <div>
                      <p className="text-xs uppercase tracking-wide text-neutral-500">{t('paymentSuccess.amountPaid')}</p>
                      <p className="text-xl font-bold text-white">{paymentData.amount} €</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <div>
                      <p className="text-xs uppercase tracking-wide text-neutral-500">{t('paymentSuccess.status')}</p>
                      <p className="text-white">{t('paymentSuccess.confirmed')}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <p className="text-neutral-400 text-sm">
                  {t('paymentSuccess.emailNotice')}
                </p>
                {paymentData?.provider && (
                  <p className="text-neutral-500 text-xs">
                    {t('paymentSuccess.paidVia', { provider: 'Stripe' })}
                  </p>
                )}
                <Link to="/">
                  <Button className="w-full bg-white text-black hover:bg-neutral-200 py-6 font-medium">
                    {t('paymentSuccess.backHome')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}

          {status === 'expired' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass p-12 text-center"
            >
              <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-yellow-400" />
              </div>
              <h1 className="font-heading text-3xl font-bold text-white mb-2">
                {t('paymentSuccess.sessionExpired')}
              </h1>
              <p className="text-neutral-400 mb-8">
                {t('paymentSuccess.sessionExpiredDesc')}
              </p>
              <Link to="/reserver">
                <Button className="w-full bg-white text-black hover:bg-neutral-200 py-6 font-medium">
                  {t('paymentSuccess.newBooking')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          )}

          {(status === 'error' || status === 'timeout') && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass p-12 text-center"
            >
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>
              <h1 className="font-heading text-3xl font-bold text-white mb-2">
                {status === 'timeout' ? t('paymentSuccess.timeoutTitle') : t('paymentSuccess.errorTitle')}
              </h1>
              <p className="text-neutral-400 mb-8">
                {error || t('paymentSuccess.errorDefault')}
              </p>
              <div className="space-y-3">
                <Link to="/reserver">
                  <Button className="w-full bg-white text-black hover:bg-neutral-200 py-6 font-medium">
                    {t('paymentSuccess.tryAgain')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="w-full border-neutral-700 text-white hover:bg-white/5 py-6">
                    {t('paymentSuccess.contactUs')}
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PaymentSuccessPage;
